import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createMetric, deleteMetric, fetchAllMetrics, updateMetric } from '../api/metrics-api';
import { QK } from '../lib/query-keys';
import type { CalibrationMetric } from '../lib/types';
import { useCurrentBotId } from './use-current-bot-id';

// Metrics are bot-scoped server-side — `fetchAllMetrics` sends `x-bot-id` — so
// the current bot belongs in the key. Without it a bot switch keeps serving
// the previous bot's metrics, and the mutation writers below would patch that
// stale list. Read reactively so the switch lands on the next render.
export function useMetrics() {
  const botId = useCurrentBotId();
  return useQuery({
    queryKey: QK.calibration.metrics(botId),
    queryFn: ({ signal }) => fetchAllMetrics(signal),
  });
}

export function useCreateMetric() {
  const queryClient = useQueryClient();
  const botId = useCurrentBotId();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createMetric(payload),
    onSuccess: (created) => {
      queryClient.setQueryData<CalibrationMetric[]>(QK.calibration.metrics(botId), (prev) => [
        ...(prev ?? []),
        created,
      ]);
    },
  });
}

export function useUpdateMetric() {
  const queryClient = useQueryClient();
  const botId = useCurrentBotId();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      updateMetric(id, payload),
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData<CalibrationMetric[]>(QK.calibration.metrics(botId), (prev) =>
        (prev ?? []).map((m) => (m.id === id ? { ...m, ...updated } : m))
      );
    },
  });
}

export function useDeleteMetric() {
  const queryClient = useQueryClient();
  const botId = useCurrentBotId();
  return useMutation({
    mutationFn: (id: string) => deleteMetric(id),
    onSuccess: (_res, id) => {
      queryClient.setQueryData<CalibrationMetric[]>(QK.calibration.metrics(botId), (prev) =>
        (prev ?? []).filter((m) => m.id !== id)
      );
    },
  });
}
